/**
 * PROTOTYPE variant D: the upstream t3code composition.
 *
 * Design plan, reviewed against the brief before writing:
 * - Colour: semantic tokens only. Canvas `background`, hairlines `border/60`,
 *   labels `muted-foreground/70`, the status ladder for tone. No accent added.
 * - Type: the platform UI stack. Section heading 18px semibold (house);
 *   stat label 11px tracked uppercase (upstream idiom, pinned by the brief);
 *   stat value 18px semibold tabular; tables 12px.
 * - Layout: the expanded rail (max-w-6xl) and 40px between sections, as
 *   upstream's Diagnostics page. Each section is heading row, then a stat
 *   strip of at most four blocks, then a hairline table for the rest.
 *   Everything left aligned. The target picker and Probe sit in the first
 *   section's header row, so the page has no separate toolbar.
 * - Principle: one loud element, the health verdict in its tone colour.
 *   Provenance is a "Checked <time>" label per section, never a paragraph.
 *   Hairlines only where they encode a grid; whitespace separates sections.
 *   When two CMMD sources disagree, that is one row under the first strip.
 */
import { StatusDot, formatTimestamp } from "../DiagnosticsPanelPrimitives";
import { DiagnosticsTargetPicker } from "../DiagnosticsTargetPicker";
import { SettingsSection } from "../settingsLayout";
import type { PrototypeFact, PrototypeFactGroup } from "./diagnosticsPrototypeModel";
import type { DiagnosticsPrototypeVariantProps } from "./diagnosticsPrototypeProps";
import {
  CheckRow,
  EmptyRows,
  PrototypeTable,
  SectionHeaderAction,
  StatBlock,
  StatsGrid,
} from "./prototypeStatStrip";

const STRIP_SIZE = 4;

/** Facts with a tone go on the strip first; the rest fill the table. */
function splitGroup(group: PrototypeFactGroup): {
  readonly strip: ReadonlyArray<PrototypeFact>;
  readonly rows: ReadonlyArray<PrototypeFact>;
} {
  const toned = group.facts.filter((fact) => fact.tone !== undefined);
  const plain = group.facts.filter((fact) => fact.tone === undefined);
  const strip = [...toned, ...plain].slice(0, STRIP_SIZE);
  const rows = group.facts.filter((fact) => !strip.includes(fact));
  return { strip, rows };
}

function FactSection({
  group,
  headerAction,
}: {
  readonly group: PrototypeFactGroup;
  readonly headerAction: React.ReactNode;
}) {
  const { strip, rows } = splitGroup(group);
  return (
    <SettingsSection title={group.title} headerAction={headerAction}>
      <StatsGrid>
        {strip.map((fact) => (
          <StatBlock
            key={fact.label}
            label={fact.label}
            value={fact.value}
            tone={fact.tone ?? "info"}
            mono={fact.mono ?? false}
          />
        ))}
      </StatsGrid>
      {rows.length > 0 ? (
        <PrototypeTable headers={["Check", "Result", "Observed"]}>
          {rows.map((fact) => (
            <CheckRow
              key={fact.label}
              name={fact.label}
              result={fact.value}
              tone={fact.tone ?? "muted"}
              observedAt={group.observedAt}
            />
          ))}
        </PrototypeTable>
      ) : null}
    </SettingsSection>
  );
}

export function DiagnosticsPrototypeVariantD({
  model,
  targets,
  refresh,
}: DiagnosticsPrototypeVariantProps) {
  const [first, ...rest] = model.groups;
  const firstSplit = first ? splitGroup(first) : { strip: [], rows: [] };
  const verdictStrip = firstSplit.strip.slice(0, STRIP_SIZE - 1);
  const firstRows = first ? first.facts.filter((fact) => !verdictStrip.includes(fact)) : [];

  return (
    <>
      <SettingsSection
        title={model.target?.label ?? "Diagnostics"}
        headerAction={
          <div className="flex items-center gap-2">
            <DiagnosticsTargetPicker
              groups={targets.groups}
              active={targets.active}
              onSelect={targets.onSelect}
            />
            <SectionHeaderAction observedAt={model.verdict.observedAt} refresh={refresh} />
          </div>
        }
      >
        <StatsGrid>
          <StatBlock
            label="Health"
            value={model.verdict.label}
            tone={model.verdict.tone === "muted" ? "info" : model.verdict.tone}
          />
          {verdictStrip.map((fact) => (
            <StatBlock
              key={fact.label}
              label={fact.label}
              value={fact.value}
              tone={fact.tone ?? "info"}
              mono={fact.mono ?? false}
            />
          ))}
        </StatsGrid>
        <p className="px-4 pt-3 text-[13px] leading-[1.45] text-muted-foreground/80 sm:px-5">
          {model.verdict.sentence} {model.verdict.source}
        </p>
        {model.verdict.disagreement ? (
          <p className="flex items-start gap-2 px-4 pt-2 text-[13px] text-warning-foreground sm:px-5">
            <span className="mt-1.5">
              <StatusDot tone="warning" />
            </span>
            {model.verdict.disagreement}
          </p>
        ) : null}
        {firstRows.length > 0 ? (
          <div className="pt-3">
            <PrototypeTable headers={["Check", "Result", "Observed"]}>
              {firstRows.map((fact) => (
                <CheckRow
                  key={fact.label}
                  name={fact.label}
                  result={fact.value}
                  tone={fact.tone ?? "muted"}
                  observedAt={first?.observedAt ?? null}
                />
              ))}
            </PrototypeTable>
          </div>
        ) : null}
      </SettingsSection>

      {rest.map((group) => (
        <FactSection
          key={group.id}
          group={group}
          headerAction={<SectionHeaderAction observedAt={group.observedAt} />}
        />
      ))}

      <SettingsSection title="Events">
        {model.events.length > 0 ? (
          <PrototypeTable headers={["Event", "Detail", "Time"]}>
            {model.events.map((event) => (
              <tr key={`${event.title}-${event.at}`}>
                <td className="px-4 py-2.5 align-top font-medium text-foreground sm:pl-5">
                  <span className="inline-flex items-center gap-2">
                    <StatusDot tone={event.tone} />
                    {event.title}
                  </span>
                </td>
                <td className="px-4 py-2.5 align-top text-muted-foreground">{event.detail}</td>
                <td className="px-4 py-2.5 text-right align-top font-mono text-[11px] whitespace-nowrap text-muted-foreground tabular-nums sm:pr-5">
                  {formatTimestamp(event.at)}
                </td>
              </tr>
            ))}
          </PrototypeTable>
        ) : (
          <EmptyRows label="No connection failures or blocked checks on record." />
        )}
      </SettingsSection>
    </>
  );
}
